import { useEffect, useRef } from 'react';
import { useUIStore } from '#store';
import rollbar from 'src/services/rollbar.service';

/**
 * Custom hook to monitor loading times and report to Rollbar
 * when loading exceeds 10 seconds
 */
export function useLoadingMonitor(isLoading) {
  const {
    user,
    currentPage,
    checkoutStage,
    outing,
    sessionInfo,
    location,
  } = useUIStore((state) => ({
    user: state.user,
    currentPage: state.currentPage,
    checkoutStage: state.checkoutStage,
    outing: state.outing,
    sessionInfo: state.sessionInfo,
    location: state.location,
  }));

  const loadingTimerRef = useRef(null);
  const loadingTrackedRef = useRef(false);

  useEffect(() => {
    if (isLoading) {
      // Reset tracking flag when loading starts
      loadingTrackedRef.current = false;
      
      // Set a timer for 10 seconds
      loadingTimerRef.current = setTimeout(() => {
        if (!loadingTrackedRef.current) {
          loadingTrackedRef.current = true;
          
          // Prepare user context
          const userContext = user ? {
            userId: user._id,
            email: user.email,
            phone: user.phone,
            name: user.firstName + ' ' + user.lastName,
          } : null;

          // Prepare outing context
          const outingContext = outing ? {
            outingId: outing._id,
            outingName: outing.name,
            outingType: outing.entity_type,
          } : null;

          // Send event to Rollbar
          rollbar.warning('Loading screen visible for more than 10 seconds', {
            loadingDuration: '10+ seconds',
            timestamp: new Date().toISOString(),
            url: window.location.href,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            user: userContext,
            currentPage: currentPage || 'unknown',
            checkoutStage: checkoutStage || 'not-in-checkout',
            outing: outingContext,
            sessionId: sessionInfo?.id,
            sessionCreatedAt: sessionInfo?.createdAt,
            location: location ? {
              country: location.country,
              state: location.state,
              city: location.city,
            } : null,
          });
        }
      }, 10000);
    } else {
      // Clear timer when loading ends
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      loadingTrackedRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [isLoading, user, currentPage, checkoutStage, outing, sessionInfo, location]);
}
